package com.eladfinish.bible.contest.ui.text

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.withStyle
import com.eladfinish.bible.contest.extensions.end
import com.eladfinish.bible.contest.extensions.start
import com.eladfinish.bible.contest.platform.composePlatformSpecific
import com.eladfinish.bible.contest.platform.platformSpecific
import com.eladfinish.bible.contest.utils.RegexUtils.UNDERSCORES

/**
 * On iOS each underscore is replaced by one EM DASH character (U+2014, '—'),
 * which is a 1-to-1 substitution.  This means the rendered string has the same
 * length as the original, so span indices require no remapping.
 *
 * On Android the factor is also 1 (underscore glyphs kept, Serif font applied).
 *
 * The constant is preserved so that [buildOriginalToExpandedPositionMap] and its
 * tests remain valid. With factor = 1 the map is always the identity.
 */
internal const val IOS_UNDERSCORE_EXPANSION_FACTOR = 1

/**
 * On iOS, underscore sequences are rendered using the LOWER ONE EIGHTH BLOCK
 * character (U+2581, '▁'), which appears as a continuous low horizontal line.
 *
 * Each original underscore is replaced 1-to-1, preserving string length and
 * span indices while providing a visually connected placeholder across fonts.
 */
private const val IOS_BLANK_CHAR = '▁'

/**
 * Builds an index-mapping array of size `originalText.length + 1`.
 * `map[i]` is the position in the expanded string that corresponds to position `i`
 * in [originalText].
 *
 * When [expansionFactor] is 1 (Android) the map is the identity (`map[i] == i`).
 * When [expansionFactor] is 2 (iOS) each `_` occupies 2 positions in the expanded
 * string, shifting all subsequent indices accordingly.
 */
fun buildOriginalToExpandedPositionMap(originalText: String, expansionFactor: Int): IntArray {
    if (expansionFactor == 1) return IntArray(originalText.length + 1) { it }
    val map = IntArray(originalText.length + 1)
    var expandedIdx = 0
    for (i in originalText.indices) {
        map[i] = expandedIdx
        expandedIdx += if (originalText[i] == '_') expansionFactor else 1
    }
    map[originalText.length] = expandedIdx
    return map
}

/*
 * Underscore placeholders – platform-specific rendering
 *
 * In the Pyramid Crossword UI, underscore sequences ("____") are used as
 * placeholders for missing letters in questions.
 *
 * On Android, underscores can be rendered nicely by switching to a specific
 * font family, which visually connects the glyphs into a continuous line.
 * However, on iOS the same approach results in visible gaps between underscore
 * glyphs, due to platform font rendering differences that cannot be reliably
 * controlled via FontFamily alone.
 *
 * To ensure consistent and readable visuals across platforms:
 * - Android keeps the original underscore + font-family approach.
 * - iOS replaces underscore glyphs with classic underline placeholders
 *   (underlined spaces), which render as a continuous line.
 *
 * The public API in this file exposes platform-agnostic helpers
 * (e.g. toAnnotatedWithUnderscorePlaceholders / applyUnderscorePlaceholders),
 * while the actual rendering strategy is selected internally per platform.
 *
 * This allows the UI layer to remain clean, expressive, and future-proof,
 * without leaking platform-specific rendering logic into calling code.
 */

@Composable
fun String.toAnnotatedWithUnderscorePlaceholders(
    underscoreFont: FontFamily = FontFamily.Serif,
): AnnotatedString =
    composePlatformSpecific(
        forAndroid = {
            toAnnotatedWithUnderscoreFont(
                underscoreFont = underscoreFont,
            )
        },
        forIos = {
            toAnnotatedWithUnderlinePlaceholders()
        },
    )

fun AnnotatedString.Builder.applyUnderscorePlaceholders(
    sourceText: String,
    outerStyle: SpanStyle,
    underscoreFont: FontFamily = FontFamily.Serif,
) {
    platformSpecific(
        forAndroid = {
            withStyle(
                style = outerStyle,
            ) {
                append(sourceText)
            }

            applyUnderscoreFont(
                sourceText = sourceText,
                fontFamily = underscoreFont,
            )
        },
        forIos = {
            appendWithStyleAndUnderlinePlaceholders(
                sourceText = sourceText,
                outerStyle = outerStyle,
            )
        },
    )
}

// for Android
@Composable
private fun String.toAnnotatedWithUnderscoreFont(
    underscoreFont: FontFamily = FontFamily.Serif,
): AnnotatedString {
    val regex = remember { UNDERSCORES.toRegex() }

    return remember(this) {
        buildAnnotatedString {
            append(this@toAnnotatedWithUnderscoreFont)

            regex.findAll(this@toAnnotatedWithUnderscoreFont).forEach { match ->
                addStyle(
                    style = SpanStyle(fontFamily = underscoreFont),
                    start = match.start,
                    end = match.end,
                )
            }
        }
    }
}

// for Android
private fun AnnotatedString.Builder.applyUnderscoreFont(
    sourceText: String,
    fontFamily: FontFamily = FontFamily.Serif,
) {
    UNDERSCORES.toRegex()
        .findAll(sourceText)
        .forEach { match ->
            addStyle(
                style = SpanStyle(fontFamily = fontFamily),
                start = match.start,
                end = match.end,
            )
        }
}

// for iOS
@Composable
private fun String.toAnnotatedWithUnderlinePlaceholders(): AnnotatedString {
    val regex = remember { UNDERSCORES.toRegex() }

    return remember(this) {
        buildAnnotatedString {
            var lastIndex = 0

            regex.findAll(this@toAnnotatedWithUnderlinePlaceholders).forEach { match ->
                // Text before underscores
                append(this@toAnnotatedWithUnderlinePlaceholders.substring(lastIndex, match.range.first))

                // Replace each '_' with one EM DASH (1-to-1 substitution → positions preserved).
                // Underlined spaces were tried previously but TextDecoration.Underline on whitespace
                // does not render in RTL text on iOS, making blanks invisible.
                // EM DASH renders as a long horizontal bar on all fonts and clearly signals a blank.
                repeat(match.value.length) { append(IOS_BLANK_CHAR) }

                lastIndex = match.range.last + 1
            }

            // Remaining text
            if (lastIndex < this@toAnnotatedWithUnderlinePlaceholders.length) {
                append(this@toAnnotatedWithUnderlinePlaceholders.substring(lastIndex))
            }
        }
    }
}

// for iOS
private fun AnnotatedString.Builder.appendWithStyleAndUnderlinePlaceholders(
    sourceText: String,
    outerStyle: SpanStyle,
) {
    val regex = UNDERSCORES.toRegex()

    withStyle(
        style = outerStyle,
    ) {
        var lastIndex = 0

        regex.findAll(sourceText).forEach { match ->
            // Text before underscores (inherits outerStyle)
            append(sourceText.substring(lastIndex, match.range.first))

            val length = match.value.length

            // EM DASH placeholder (1-to-1, inherits outerStyle)
            repeat(length) { append(IOS_BLANK_CHAR) }

            lastIndex = match.range.last + 1
        }

        // Remaining text (inherits outerStyle)
        if (lastIndex < sourceText.length) {
            append(sourceText.substring(lastIndex))
        }
    }
}
